import { useCallback, useEffect, useState } from "react";
import { Nav } from "../components/Nav";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Lock, Users, Crown, Wand2, IndianRupee, Star, Calendar, RefreshCw, Loader2 } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const fmtCurrency = (n) => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0)}`;

export default function AdminMetrics() {
  const { user } = useAuth();
  const isAdminUser = !!user?.is_admin;
  const [secret, setSecret] = useState(() => localStorage.getItem("admin_secret") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (s) => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/admin/metrics", s ? { headers: { "X-Admin-Secret": s } } : undefined);
      setData(res);
      setAuthed(true);
      if (s) localStorage.setItem("admin_secret", s);
    } catch (err) {
      setAuthed(false);
      if (err?.response?.status === 403 && s) alert("Wrong admin secret");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdminUser) load("");
    else if (secret) load(secret);
  }, [isAdminUser, secret, load]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
        <Nav />
        <div className="max-w-md mx-auto px-6 py-24 fade-up">
          <Lock className="w-8 h-8 text-[#E3C2B0] mb-6" strokeWidth={1} />
          <h1 className="font-serif text-4xl font-light">Metrics</h1>
          <p className="mt-3 text-sm text-[#A8A6A1]">Enter the admin secret to view the funnel dashboard.</p>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="admin-metrics-secret"
            placeholder="••••••••"
            className="mt-10 w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] text-lg font-light"
          />
          <button
            onClick={() => load(secret)}
            disabled={loading || !secret}
            data-testid="admin-metrics-unlock"
            className="mt-8 w-full bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] disabled:opacity-50 text-xs uppercase tracking-[0.3em] flex items-center justify-center gap-3"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Checking...</> : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const u = data.users || {};
  const s = data.subscribers || {};
  const l = data.looks || {};
  const p = data.payments || {};
  const f = data.feedback || {};

  const convRate = u.total ? ((s.active_paying || 0) / u.total * 100).toFixed(1) : "0.0";

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-start justify-between flex-wrap gap-4 fade-up">
          <div>
            <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Founder Dashboard</span>
            <h1 className="font-serif text-5xl mt-4 font-light leading-tight">Metrics.</h1>
            <p className="mt-3 text-sm text-[#A8A6A1] flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span data-testid="metrics-generated-at">Snapshot · {new Date(data.generated_at).toLocaleString()}</span>
            </p>
          </div>
          <button
            onClick={() => load(secret)}
            disabled={loading}
            data-testid="metrics-refresh"
            className="text-xs uppercase tracking-[0.3em] border border-white/15 px-5 py-3 rounded-sm hover:bg-white/5 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />}
            Refresh
          </button>
        </div>

        {/* Top-line KPIs */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-kpis">
          <Kpi icon={<Users className="w-4 h-4" />} label="Total Users" value={fmt(u.total)} sub={`+${fmt(u.new_last_30d)} in 30d`} testid="kpi-users" />
          <Kpi icon={<Crown className="w-4 h-4" />} label="Active Payers" value={fmt(s.active_paying)} sub={`${convRate}% conversion`} testid="kpi-payers" highlight />
          <Kpi icon={<Wand2 className="w-4 h-4" />} label="Looks Generated" value={fmt(l.total)} sub={`+${fmt(l.last_30d)} in 30d`} testid="kpi-looks" />
          <Kpi icon={<IndianRupee className="w-4 h-4" />} label="Revenue (30d)" value={fmtCurrency(p.last_30d?.revenue)} sub={`${fmt(p.last_30d?.count)} paid txns`} testid="kpi-revenue" />
        </div>

        {/* Row 2 */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Subscribers" testid="panel-subscribers">
            <TierRow label="Basic (₹99)" count={s.basic} />
            <TierRow label="Standard (₹299)" count={s.standard} />
            <TierRow label="Premium (₹999)" count={s.premium} highlight />
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs uppercase tracking-[0.3em]">
              <span className="text-[#A8A6A1]">Active (unexpired)</span>
              <span className="text-[#E3C2B0]">{fmt(s.active_paying)}</span>
            </div>
          </Panel>

          <Panel title="Revenue" testid="panel-revenue">
            <TierRow label="All-time paid txns" count={fmt(p.all_time?.count)} />
            <TierRow label="All-time revenue" count={fmtCurrency(p.all_time?.revenue)} highlight />
            <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em]">
                <span className="text-[#A8A6A1]">Last 30d txns</span>
                <span className="text-[#F7F5F2]">{fmt(p.last_30d?.count)}</span>
              </div>
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em]">
                <span className="text-[#A8A6A1]">Last 30d revenue</span>
                <span className="text-[#E3C2B0]">{fmtCurrency(p.last_30d?.revenue)}</span>
              </div>
            </div>
          </Panel>

          <Panel title="Looks · Top Occasions (30d)" testid="panel-occasions">
            {(l.top_occasions_30d || []).length === 0 ? (
              <div className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] py-8 text-center">No looks in the last 30 days</div>
            ) : (
              <div className="space-y-3">
                {(l.top_occasions_30d || []).map((row, i) => (
                  <OccasionBar
                    key={row.occasion || i}
                    label={row.occasion || "—"}
                    count={row.count}
                    max={l.top_occasions_30d[0].count}
                  />
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Funnel & Signals" testid="panel-funnel">
            <TierRow label="Users w/ complete profile" count={fmt(u.profile_complete)} />
            <TierRow label="Signups (last 30d)" count={fmt(u.new_last_30d)} />
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs uppercase tracking-[0.3em]">
              <span className="text-[#A8A6A1] flex items-center gap-2"><Star className="w-3.5 h-3.5" strokeWidth={1.5} /> Avg Rating</span>
              <span className="text-[#E3C2B0]">{f.avg_rating || 0}  <span className="text-[#A8A6A1] ml-2">({fmt(f.count)} reviews)</span></span>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ icon, label, value, sub, testid, highlight }) => (
  <div
    data-testid={testid}
    className={`p-6 border rounded-sm bg-[#1A1921] ${highlight ? "border-[#E3C2B0]/60" : "border-white/10"}`}
  >
    <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] ${highlight ? "text-[#E3C2B0]" : "text-[#A8A6A1]"}`}>
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-4 font-serif text-4xl font-light text-[#F7F5F2]">{value}</div>
    <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1]">{sub}</div>
  </div>
);

const Panel = ({ title, children, testid }) => (
  <div data-testid={testid} className="p-8 border border-white/10 rounded-sm bg-[#1A1921]">
    <div className="text-[10px] uppercase tracking-[0.4em] text-[#E3C2B0] mb-6">{title}</div>
    {children}
  </div>
);

const TierRow = ({ label, count, highlight }) => (
  <div className="flex items-center justify-between py-2.5 text-sm">
    <span className="text-[#A8A6A1] font-light">{label}</span>
    <span className={`${highlight ? "text-[#E3C2B0] text-lg" : "text-[#F7F5F2]"} font-light`}>{count ?? 0}</span>
  </div>
);

const OccasionBar = ({ label, count, max }) => {
  const pct = max ? Math.max(6, (count / max) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-[#F7F5F2]/90 capitalize">{label}</span>
        <span className="text-[#A8A6A1]">{count}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full bg-[#E3C2B0]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
